'use client';

import { useTranslations } from 'next-intl';
import { ProductDetailPricesContainer, useAppLocale, useProductSet } from '@headless-workspace/domain/common/client';
import { ProductSetItemValue } from '@headless-workspace/domain/common/server';
import { GlowImage, GlowTextBody, PropsWithFontFamily } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { useProductDetailPromotionsInformation } from '../../../../../client';
import { VariantSelectorZone } from './VariantSelectorZone';

const PRODUCT_SET_IMAGE_SIZE_PX = 80;

export type ProductSetItemProps = Pick<ProductSetItemValue, 'name' | 'variants' | 'type' | 'formats' | 'currency'> &
    PropsWithFontFamily & {
        isVipBrand: boolean;
        priceAlwaysBlack: boolean;
        squaredShade: boolean;
        index: number;
    };

export const ProductSetItem: Component<ProductSetItemProps> = ({
    name,
    variants,
    type,
    formats,
    currency,
    priceAlwaysBlack,
    fontFamily,
    isVipBrand,
    squaredShade,
    index,
}) => {
    const locale = useAppLocale();
    const t = useTranslations('Discover.Pdp.information.price');

    const { getSelectedProductSetVariant, setProductSetVariant, setProductSetVariantPreview } = useProductSet();

    const selectedVariant = getSelectedProductSetVariant(index);

    const promotionInformation = useProductDetailPromotionsInformation({ promotion: selectedVariant?.promotion });

    if (!selectedVariant) {
        return null;
    }

    const { id, image, price, priorPrice, originalPrice, pricePerUnit, promotion, discountPercentage } =
        selectedVariant;

    return (
        <div className="flex flex-col gap-1">
            <div className="flex flex-row gap-1 items-start self-stretch">
                <GlowImage
                    image={image}
                    height={PRODUCT_SET_IMAGE_SIZE_PX}
                    width={PRODUCT_SET_IMAGE_SIZE_PX}
                    className={'min-w-[5rem]'}
                />
                <div>
                    <GlowTextBody fontFamily={fontFamily} fontWeight={'bold'} text={name} />
                    <ProductDetailPricesContainer
                        variant="small"
                        priorPrice={priorPrice}
                        price={price}
                        currency={currency}
                        locale={locale}
                        isVipBrand={isVipBrand}
                        pricePerUnit={pricePerUnit}
                        fontFamily={fontFamily}
                        priceAlwaysBlack={priceAlwaysBlack}
                        originalPrice={originalPrice}
                        promotionInformation={promotionInformation}
                        discountPercentageLabel={
                            discountPercentage
                                ? t('label.discountPercentage', {
                                      discountPercentage,
                                  })
                                : undefined
                        }
                        promotion={promotion}
                    />
                </div>
            </div>
            <VariantSelectorZone
                productName={name}
                fontFamily={fontFamily}
                formats={formats}
                currency={currency}
                locale={locale}
                type={type}
                variants={variants}
                squaredShade={squaredShade}
                isVipBrand={isVipBrand}
                priceAlwaysBlack={priceAlwaysBlack}
                onVariantSelected={setProductSetVariant(index)}
                selectedVariantId={id}
                setHoveredVariant={setProductSetVariantPreview(index)}
            />
        </div>
    );
};
